import { Backdrop } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import JoditEditor from 'jodit-react';
import { ReactElement, useMemo } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { FormFieldLabel } from '@/components/form/FormFieldLabel.comp.tsx';
import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { getRichTextFieldConfig } from '@/components/form/util/getRichTextFieldConfig.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { emptyFunction } from '@/utils/emptyFunction.util';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = FormPropsModel<TName> & {
  height?: number;
};

export const RichTextFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
): ReactElement<Props<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const { disabled, fullWidth, height, label, name, placeholder, ...rest } = props;
  const { validation } = useFormProviderRCPropsContext();

  const config = useMemo(
    () => getRichTextFieldConfig({ disabled, height, placeholder }),
    [disabled, height, placeholder],
  );

  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);

    return (
      <FormControl fullWidth={fullWidth ?? true} sx={{ mt: 2 }}>
        <FormFieldLabel isRequired={isRequired} label={label} />
        <Box
          sx={{
            position: 'relative',
          }}
        >
          {disabled && (
            <Backdrop
              open
              sx={{
                background: themeColors.stroke60,
                bottom: 0,
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0,
                zIndex: 100,
              }}
            />
          )}
          <JoditEditor
            config={config}
            tabIndex={1}
            {...field}
            {...rest}
            onBlur={newContent => field.onChange(newContent)}
            onChange={emptyFunction}
          />
        </Box>
        <TextError error={error} />
      </FormControl>
    );
  };

  return <Controller<TFieldValues, TName> control={control} name={name} render={render} />;
};
